import {
  Box,
  Button,
  createListCollection,
  HStack,
  Icon,
  IconButton,
  Input,
  Popover,
  Portal,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react'
import { CircleMinus, Clock, ClockAlert, ClockCheck, Plus } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'

import type { HotelsApi } from '../../api/hotelsApi'
import { useHotelsI18n } from '../../i18n'
import {
  useBulkDeleteWakeupCalls,
  useCreateWakeupCall,
  useDeleteWakeupCall,
} from '../../queries/hooks'
import { useRoomWakeupData } from '../../queries/roomWakeupData'
import type { EnrichedRoom, HospitalityPermissions } from '../../types/rooms'
import type { WakeupCall } from '../../types/wakeup'
import { getHotelsPlaceholderText } from '../../utils/display'
import {
  formatIsoInTimeZone,
  getWakeupInstantIso,
} from '../../utils/formatting'
import {
  filterTimeOptions,
  useDateOptions,
  useTimeOptions,
} from '../../utils/timeOptions'
import { type WakeupNotificationTone } from '../../utils/wakeupDashboard'
import { pbxWallTimeToUtcIso } from '../../utils/wakeupTimezone'
import { WakeupStatusBadge } from '../wakeup/WakeupStatusBadge'

interface WakeUpCallPopoverProps {
  room: EnrichedRoom
  api: HotelsApi
  pbxUuid: string
  /** IANA PBX timezone (system settings). */
  pbxTimeZone: string
  toast: ToastLike
  permissions: HospitalityPermissions
  prefetchedTone?: WakeupNotificationTone
}

interface WakeUpCallPopoverContentProps {
  room: EnrichedRoom
  api: HotelsApi
  pbxUuid: string
  pbxTimeZone: string
  toast: ToastLike
  permissions: HospitalityPermissions
  onNotificationToneChange: (tone: WakeupNotificationTone) => void
}

const WakeUpCallPopoverContent = ({
  room,
  api,
  pbxUuid,
  pbxTimeZone,
  toast,
  permissions,
  onNotificationToneChange,
}: WakeUpCallPopoverContentProps) => {
  const { text, locale } = useHotelsI18n()
  const placeholder = getHotelsPlaceholderText(text)
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedTime, setSelectedTime] = useState('')
  const [timeFilter, setTimeFilter] = useState('')
  const [deletingWakeupId, setDeletingWakeupId] = useState<
    number | string | null
  >(null)
  const [hoveredWakeupId, setHoveredWakeupId] = useState<
    number | string | null
  >(null)
  const [focusedWakeupId, setFocusedWakeupId] = useState<
    number | string | null
  >(null)

  const dateOptions = useDateOptions({
    locale,
    pbxTimeZone,
    includeRelativeLabels: true,
    todayLabel: text('hotels.common.today', 'Today'),
    tomorrowLabel: text('hotels.common.tomorrow', 'Tomorrow'),
  })
  const timeOptions = useTimeOptions(locale)
  const dateCollection = useMemo(
    () => createListCollection({ items: dateOptions }),
    [dateOptions],
  )

  const filteredTimeOptions = useMemo(
    () => filterTimeOptions(timeFilter, timeOptions),
    [timeFilter, timeOptions],
  )

  const timeCollection = useMemo(
    () => createListCollection({ items: filteredTimeOptions }),
    [filteredTimeOptions],
  )

  const {
    wakeups: roomWakeups,
    tone: roomWakeupTone,
    isLoading: isWakeupsLoading,
    isError: isWakeupsError,
  } = useRoomWakeupData({
    api,
    pbxUuid,
    room,
    options: {
      enabled: !!pbxUuid,
    },
  })

  useEffect(() => {
    if (isWakeupsLoading || isWakeupsError) return
    onNotificationToneChange(roomWakeupTone)
  }, [
    isWakeupsError,
    isWakeupsLoading,
    onNotificationToneChange,
    roomWakeupTone,
  ])

  const canDeleteWakeups =
    permissions.canDeleteQueues || permissions.canEditQueues

  const deleteMutation = useDeleteWakeupCall({
    api,
    options: {
      onSuccess: () =>
        toast.create({
          title: text(
            'hotels.wakeupScheduler.cancelSuccess',
            'Wake-up call canceled',
          ),
          type: 'success',
        }),
      onError: () =>
        toast.create({
          title: text(
            'hotels.wakeupScheduler.cancelFailed',
            'Failed to cancel wake-up call',
          ),
          type: 'error',
        }),
      onSettled: () => setDeletingWakeupId(null),
    },
  })

  const bulkDeleteMutation = useBulkDeleteWakeupCalls({
    api,
    options: {
      onSuccess: (data) => {
        toast.create({
          title: text(
            'hotels.dashboard.wakeupPopover.clearAllSuccess',
            '{{count}} wake-up{{suffix}} canceled',
            {
              count: data.canceled_count,
              suffix: data.canceled_count !== 1 ? 's' : '',
            },
          ),
          type: 'success',
        })
      },
      onError: () =>
        toast.create({
          title: text(
            'hotels.dashboard.wakeupPopover.clearAllFailed',
            'Failed to clear wake-ups',
          ),
          type: 'error',
        }),
    },
  })

  const createMutation = useCreateWakeupCall({
    api,
    options: {
      onSuccess: () => {
        toast.create({
          title: text(
            'hotels.dashboard.wakeupPopover.scheduleSuccess',
            'Wake-up call scheduled',
          ),
          type: 'success',
        })
        setSelectedDate('')
        setSelectedTime('')
      },
      onError: () =>
        toast.create({
          title: text(
            'hotels.dashboard.wakeupPopover.scheduleFailed',
            'Failed to schedule wake-up',
          ),
          type: 'error',
        }),
    },
  })

  const handleAdd = useCallback(() => {
    if (!permissions.canNewQueues) return
    if (!selectedDate || !selectedTime) return
    const wuc_timestamp = pbxWallTimeToUtcIso(
      selectedDate,
      selectedTime,
      pbxTimeZone,
    )
    if (!wuc_timestamp) {
      toast.create({
        title: text(
          'hotels.wakeupScheduler.invalidTime',
          'That time does not exist on the selected date (DST transition). Please choose a different time.',
        ),
        type: 'warning',
      })
      return
    }
    createMutation.mutate({
      pbx_uuid: pbxUuid,
      extension_uuid: room.extension_uuid,
      wuc_timestamp,
    })
  }, [
    permissions.canNewQueues,
    selectedDate,
    selectedTime,
    pbxUuid,
    pbxTimeZone,
    room.extension_uuid,
    createMutation,
    toast,
    text,
  ])

  const handleClearAll = useCallback(() => {
    if (!permissions.canDeleteQueues && !permissions.canEditQueues) return
    bulkDeleteMutation.mutate({
      extensionUuid: room.extension_uuid,
      pbxUuid,
    })
  }, [
    permissions.canDeleteQueues,
    permissions.canEditQueues,
    bulkDeleteMutation,
    room.extension_uuid,
    pbxUuid,
  ])

  const handleDeleteWakeup = useCallback(
    (id: number | string) => {
      if (!canDeleteWakeups) return
      setDeletingWakeupId(id)
      deleteMutation.mutate({ id })
    },
    [canDeleteWakeups, deleteMutation],
  )

  const selectedTimeLabel = useMemo(
    () => timeOptions.find((o) => o.value === selectedTime)?.label,
    [timeOptions, selectedTime],
  )

  const isBusy =
    deleteMutation.isPending ||
    bulkDeleteMutation.isPending ||
    createMutation.isPending

  return (
    <Popover.Content
      w="380px"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <Popover.Header p={3} borderBottomWidth={1}>
        <HStack justify="space-between" w="full">
          <HStack gap={2}>
            <Icon as={Clock} boxSize={4} color="fg.subtle" />
            <Text fontWeight="semibold" fontSize="sm">
              {text('hotels.dashboard.wakeupPopover.title', 'Wake-Up Calls')}
            </Text>
            <Text fontSize="sm" color="fg.muted">
              {room.number}
            </Text>
          </HStack>
          {roomWakeups.length > 0 && canDeleteWakeups && (
            <Button
              size="xs"
              variant="subtle"
              borderRadius="l2"
              colorPalette="error"
              color="fg.error"
              _disabled={{ color: 'fg.error' }}
              onClick={handleClearAll}
              loading={bulkDeleteMutation.isPending}
            >
              {text('hotels.dashboard.wakeupPopover.clearAll', 'Clear All?')}
            </Button>
          )}
        </HStack>
      </Popover.Header>

      <Popover.Body p={3}>
        <VStack align="stretch" gap={3}>
          <HStack gap={2}>
            <Select.Root
              collection={dateCollection}
              size="sm"
              flex="1"
              disabled={!permissions.canNewQueues}
              value={selectedDate ? [selectedDate] : []}
              onValueChange={(e) => setSelectedDate(e.value[0] ?? '')}
            >
              <Select.Trigger borderRadius="l2">
                <Select.ValueText
                  placeholder={text('hotels.common.dayPlaceholder', 'Day...')}
                />
              </Select.Trigger>
              <Portal>
                <Select.Positioner>
                  <Select.Content>
                    {dateCollection.items.map((opt) => (
                      <Select.Item key={opt.value} item={opt}>
                        {opt.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Portal>
            </Select.Root>

            <Select.Root
              collection={timeCollection}
              size="sm"
              flex="1"
              disabled={!permissions.canNewQueues}
              value={selectedTime ? [selectedTime] : []}
              onValueChange={(e) => {
                setSelectedTime(e.value[0] ?? '')
                setTimeFilter('')
              }}
              onOpenChange={(e) => {
                if (!e.open) setTimeFilter('')
              }}
            >
              <Select.Trigger borderRadius="l2">
                <Select.ValueText>
                  {selectedTimeLabel ??
                    text('hotels.common.timePlaceholder', 'Time...')}
                </Select.ValueText>
              </Select.Trigger>
              <Portal>
                <Select.Positioner>
                  <Select.Content>
                    <Input
                      size="sm"
                      borderRadius="l2"
                      placeholder={text(
                        'hotels.common.filterTimesPlaceholder',
                        'Filter times...',
                      )}
                      value={timeFilter}
                      onChange={(e) => setTimeFilter(e.target.value)}
                      mx={1}
                      my={1}
                      width="calc(100% - 8px)"
                    />
                    {timeCollection.items.map((opt) => (
                      <Select.Item key={opt.value} item={opt}>
                        {opt.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Portal>
            </Select.Root>

            {permissions.canNewQueues && (
              <IconButton
                aria-label={text(
                  'hotels.dashboard.wakeupPopover.addAria',
                  'Add wake-up call',
                )}
                size="sm"
                borderRadius="l2"
                variant="solid"
                colorPalette="brand"
                color="fg.inverted"
                _disabled={{ color: 'fg.inverted' }}
                onClick={handleAdd}
                disabled={!selectedDate || !selectedTime || isBusy}
                loading={createMutation.isPending}
              >
                <Icon as={Plus} boxSize={4} color="fg.inverted" />
              </IconButton>
            )}
          </HStack>

          {isWakeupsLoading ? (
            <Text fontSize="xs" color="fg.muted" textAlign="center" py={2}>
              {text('hotels.dashboard.wakeupPopover.loading', 'Loading...')}
            </Text>
          ) : roomWakeups.length === 0 ? (
            <Text fontSize="xs" color="fg.muted" textAlign="center" py={2}>
              {text(
                'hotels.dashboard.wakeupPopover.none',
                'No active wake-up calls',
              )}
            </Text>
          ) : (
            <VStack align="stretch" gap={1}>
              <HStack fontSize="xs" color="fg.muted" px={1}>
                <Text flex={1}>
                  {text('hotels.dashboard.wakeupPopover.schedule', 'Schedule')}
                </Text>
                <Text w="70px">
                  {text('hotels.wakeupScheduler.status', 'Status')}
                </Text>
                <Box w="24px" />
              </HStack>
              {roomWakeups.map((wc: WakeupCall) => {
                const isRowActive =
                  hoveredWakeupId === wc.id || focusedWakeupId === wc.id
                const wakeupIso = getWakeupInstantIso(wc)
                const wakeupTimeLabel = wakeupIso
                  ? formatIsoInTimeZone(wakeupIso, locale, pbxTimeZone)
                  : null
                return (
                  <HStack
                    key={wc.id}
                    fontSize="xs"
                    px={1}
                    py={1}
                    borderRadius="sm"
                    _hover={{ bg: 'bg.subtle' }}
                    onMouseEnter={() => setHoveredWakeupId(wc.id)}
                    onMouseLeave={() => setHoveredWakeupId(null)}
                    onFocus={() => setFocusedWakeupId(wc.id)}
                    onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
                      if (e.currentTarget.contains(e.relatedTarget)) return
                      setFocusedWakeupId((current) =>
                        current === wc.id ? null : current,
                      )
                    }}
                  >
                    <Text flex={1}>{wakeupTimeLabel ?? placeholder}</Text>
                    <Box w="70px" mr={1}>
                      <WakeupStatusBadge
                        displayStatus={wc.display_status ?? 'scheduled'}
                      />
                    </Box>
                    {canDeleteWakeups && (
                      <IconButton
                        aria-label={
                          wakeupTimeLabel
                            ? text(
                                'hotels.dashboard.wakeupPopover.cancelAriaWithTime',
                                'Cancel wake-up at {{time}}',
                                { time: wakeupTimeLabel },
                              )
                            : text(
                                'hotels.dashboard.wakeupPopover.cancelAria',
                                'Cancel wake-up',
                              )
                        }
                        size="2xs"
                        variant="ghost"
                        borderRadius="l2"
                        color="fg.error"
                        _hover={{ bg: 'bg.subtle', color: 'fg.error' }}
                        _disabled={{ color: 'fg.error' }}
                        opacity={
                          isRowActive || deletingWakeupId === wc.id ? 1 : 0
                        }
                        pointerEvents={isRowActive ? 'auto' : 'none'}
                        transition="opacity 0.12s ease"
                        onClick={() => handleDeleteWakeup(wc.id)}
                        loading={
                          deleteMutation.isPending && deletingWakeupId === wc.id
                        }
                        disabled={bulkDeleteMutation.isPending}
                      >
                        <Icon
                          as={CircleMinus}
                          boxSize={3.5}
                          color="currentColor"
                        />
                      </IconButton>
                    )}
                  </HStack>
                )
              })}
            </VStack>
          )}
        </VStack>
      </Popover.Body>
    </Popover.Content>
  )
}

export const WakeUpCallPopover = ({
  room,
  api,
  pbxUuid,
  pbxTimeZone,
  toast,
  permissions,
  prefetchedTone = null,
}: WakeUpCallPopoverProps) => {
  const { text } = useHotelsI18n()
  const [isOpen, setIsOpen] = useState(false)
  const [notificationTone, setNotificationTone] =
    useState<WakeupNotificationTone>(null)
  const wakeupCount = room.wakeup_calls_count ?? 0

  const effectiveTone: WakeupNotificationTone =
    wakeupCount > 0 ? (notificationTone ?? prefetchedTone ?? null) : null

  const wakeupBg =
    effectiveTone === 'cleared'
      ? 'blue.100'
      : effectiveTone === 'tried'
        ? 'orange.100'
        : 'bg.muted'
  const wakeupHoverBg =
    effectiveTone === 'cleared'
      ? 'blue.200'
      : effectiveTone === 'tried'
        ? 'orange.200'
        : 'bg.subtle'
  const wakeupIconColor =
    effectiveTone === 'cleared'
      ? 'blue.700'
      : effectiveTone === 'tried'
        ? 'orange.700'
        : 'fg.subtle'
  const wakeupIcon =
    effectiveTone === 'cleared'
      ? ClockCheck
      : effectiveTone === 'tried'
        ? ClockAlert
        : Clock
  const triggerLabel =
    effectiveTone === 'cleared'
      ? text(
          'hotels.dashboard.wakeupPopover.manageAriaCleared',
          'Manage wake-up calls (cleared)',
        )
      : effectiveTone === 'tried'
        ? text(
            'hotels.dashboard.wakeupPopover.manageAriaPending',
            'Manage wake-up calls (pending)',
          )
        : text(
            'hotels.dashboard.wakeupPopover.manageAria',
            'Manage wake-up calls',
          )

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(details) => setIsOpen(details.open)}
      positioning={{ placement: 'right-start' }}
    >
      <Popover.Trigger asChild>
        <Box
          as="button"
          position="relative"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          aria-label={triggerLabel}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          p={1.5}
          borderRadius="lg"
          bg={wakeupBg}
          _hover={{ bg: wakeupHoverBg }}
        >
          <Icon as={wakeupIcon} boxSize={5} color={wakeupIconColor} />
        </Box>
      </Popover.Trigger>
      {isOpen && (
        <Portal>
          <Popover.Positioner>
            <WakeUpCallPopoverContent
              room={room}
              api={api}
              pbxUuid={pbxUuid}
              pbxTimeZone={pbxTimeZone}
              toast={toast}
              permissions={permissions}
              onNotificationToneChange={setNotificationTone}
            />
          </Popover.Positioner>
        </Portal>
      )}
    </Popover.Root>
  )
}
